import { useState, useEffect, useRef, useCallback } from 'react';

const STORAGE_PREFIX = 'supplytrack_draft_v1__';

/**
 * useLocalDraft — auto-saves state to localStorage so back-office work isn't
 * lost when staff navigates to POS to serve a customer.
 *
 * @param {string} key       Stable identifier per draft (e.g. 'receive_stock', 'po_edit_<poId>')
 * @param {*}      initial   Initial value if no draft exists
 * @param {object} options
 *   - skipRestore: if true, don't read from storage on mount
 *   - debounceMs: how long to wait before writing (default 250)
 *
 * Returns: [value, setValue, { hasDraft, clearDraft, savedAt }]
 */
export function useLocalDraft(key, initial, options = {}) {
  const { skipRestore = false, debounceMs = 250 } = options;
  const storageKey = STORAGE_PREFIX + key;

  // Initialise from localStorage if present
  const [value, setValueRaw] = useState(() => {
    if (skipRestore) return initial;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw == null) return initial;
      const parsed = JSON.parse(raw);
      // Expect { v: <data>, t: <timestamp> }
      return parsed && Object.prototype.hasOwnProperty.call(parsed, 'v') ? parsed.v : initial;
    } catch {
      return initial;
    }
  });

  const [savedAt, setSavedAt] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed?.t || null;
    } catch {
      return null;
    }
  });

  const timerRef = useRef(null);
  const latestRef = useRef(value);
  latestRef.current = value;

  // Debounced write
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      try {
        const payload = JSON.stringify({ v: latestRef.current, t: Date.now() });
        localStorage.setItem(storageKey, payload);
        setSavedAt(Date.now());
      } catch (err) {
        // Quota or JSON failure — silently ignore
        console.warn('useLocalDraft: failed to persist', storageKey, err);
      }
    }, debounceMs);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [value, storageKey, debounceMs]);

  // Persist on unload too (covers tab close mid-debounce)
  useEffect(() => {
    const handler = () => {
      try {
        localStorage.setItem(
          storageKey,
          JSON.stringify({ v: latestRef.current, t: Date.now() })
        );
      } catch {
        // ignore
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [storageKey]);

  const clearDraft = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
      setSavedAt(null);
    } catch {
      // ignore
    }
  }, [storageKey]);

  // Detect if a non-empty draft exists in storage
  const hasDraft = (() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      const v = parsed?.v;
      if (v == null) return false;
      if (Array.isArray(v)) return v.length > 0;
      if (typeof v === 'object') return Object.keys(v).length > 0;
      return Boolean(v);
    } catch {
      return false;
    }
  })();

  return [value, setValueRaw, { hasDraft, clearDraft, savedAt }];
}

/** Helper: list all active drafts (for an "unfinished work" dashboard) */
export function listActiveDrafts() {
  const out = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(STORAGE_PREFIX)) {
        try {
          const v = JSON.parse(localStorage.getItem(k));
          out.push({ key: k.replace(STORAGE_PREFIX, ''), savedAt: v?.t, data: v?.v });
        } catch {
          // ignore parse errors
        }
      }
    }
  } catch {
    // ignore storage errors
  }
  return out;
}
